import React, { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import { getCareOrchestrationCases } from 'src/supportPortal/services/careOrchestrationService'

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'INTAKE_IN_PROGRESS', label: 'Intake in progress' },
  { value: 'TRIAGED', label: 'Triaged' },
  { value: 'AWAITING_BOOKING', label: 'Awaiting booking' },
  { value: 'CALLBACK_REQUESTED', label: 'Callback requested' },
  { value: 'ESCALATED_URGENT', label: 'Escalated urgent' },
]

const CHANNEL_OPTIONS = [
  { value: '', label: 'All channels' },
  { value: 'WEB', label: 'Portal' },
  { value: 'WHATSAPP', label: 'WhatsApp' },
  { value: 'CALL', label: 'Call' },
]

function cleanPhone(rawValue) {
  return rawValue.replace(/[^\d+]/g, '').trim()
}

function buildWhatsappLeadUrl(phone) {
  const digits = phone?.replace(/[^\d]/g, '')
  return digits ? `https://wa.me/${digits}` : null
}

function ResultCard({ item }) {
  const careCase = item?.careCase || {}
  const lead = item?.lead || {}
  const recommendation = item?.recommendation || {}
  const triageAssessment = item?.triageAssessment || {}
  const referralTask = item?.referralTask || {}
  const whatsappLeadUrl = buildWhatsappLeadUrl(lead.phone)

  return (
    <Card variant="outlined" sx={{ borderRadius: 3 }}>
      <CardContent>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1.5 }}>
          <Chip label={careCase.channel || 'UNKNOWN'} size="small" color="primary" variant="outlined" />
          <Chip label={careCase.status || 'UNKNOWN'} size="small" color="secondary" variant="outlined" />
          {careCase.provisionalPathway ? (
            <Chip label={careCase.provisionalPathway} size="small" variant="outlined" />
          ) : null}
        </Stack>

        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {lead.name || 'Unnamed lead'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {careCase.id}
        </Typography>

        <Stack spacing={1}>
          <Typography variant="body2">
            <strong>Phone:</strong> {lead.phone || 'Not captured'}
          </Typography>
          <Typography variant="body2">
            <strong>City:</strong> {careCase.city || lead.city || 'Not captured'}
          </Typography>
          <Typography variant="body2">
            <strong>Language:</strong> {careCase.preferredLanguage || lead.preferredLanguage || 'Not captured'}
          </Typography>
          <Typography variant="body2">
            <strong>Concern:</strong> {careCase.presentingConcernText || 'Not captured'}
          </Typography>
          <Typography variant="body2">
            <strong>Next step:</strong> {item?.nextActionLabel || careCase.recommendedNextStep || 'Pending'}
          </Typography>
          <Typography variant="body2">
            <strong>Urgency:</strong> {careCase.urgencyLevel || triageAssessment.severityBand || 'Pending'}
          </Typography>
          {careCase.preferredCallbackTime ? (
            <Typography variant="body2">
              <strong>Preferred callback time:</strong> {careCase.preferredCallbackTime}
            </Typography>
          ) : null}
          {recommendation.userFacingSummary ? (
            <Typography variant="body2">
              <strong>User summary:</strong> {recommendation.userFacingSummary}
            </Typography>
          ) : null}
          {referralTask.reason ? (
            <Typography variant="body2">
              <strong>Referral task:</strong> {referralTask.reason}
            </Typography>
          ) : null}
        </Stack>

        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 2 }}>
          {item?.bookingLink ? (
            <Button
              component={Link}
              href={item.bookingLink}
              target="_blank"
              rel="noreferrer"
              variant="contained"
              size="small"
            >
              Booking link
            </Button>
          ) : null}
          {item?.humanSupportWhatsappUrl ? (
            <Button
              component={Link}
              href={item.humanSupportWhatsappUrl}
              target="_blank"
              rel="noreferrer"
              variant="outlined"
              size="small"
            >
              Human support WhatsApp
            </Button>
          ) : null}
          {whatsappLeadUrl ? (
            <Button
              component={Link}
              href={whatsappLeadUrl}
              target="_blank"
              rel="noreferrer"
              variant="outlined"
              size="small"
            >
              Message lead
            </Button>
          ) : null}
        </Stack>
      </CardContent>
    </Card>
  )
}

export default function CareOrchestrationCasesPage() {
  const [draftFilters, setDraftFilters] = useState({
    status: '',
    channel: '',
    leadPhone: '',
  })
  const [filters, setFilters] = useState({
    status: '',
    channel: '',
    leadPhone: '',
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [items, setItems] = useState([])

  useEffect(() => {
    let cancelled = false

    async function load() {
      setLoading(true)
      setError('')
      try {
        const responseItems = await getCareOrchestrationCases(filters)
        if (!cancelled) {
          setItems(responseItems)
        }
      } catch (loadError) {
        if (!cancelled) {
          setError(loadError?.message || 'Unable to load care orchestration cases.')
          setItems([])
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [filters])

  function applyFilters() {
    setFilters({
      status: draftFilters.status,
      channel: draftFilters.channel,
      leadPhone: cleanPhone(draftFilters.leadPhone),
    })
  }

  function clearFilters() {
    const cleared = {
      status: '',
      channel: '',
      leadPhone: '',
    }
    setDraftFilters(cleared)
    setFilters(cleared)
  }

  return (
    <>
      <SupportHeader heading="Care Orchestration Cases" showBackButton />
      <Box sx={{ p: { xs: 2, md: 3 }, display: 'grid', gap: 2.5 }}>
        <Card variant="outlined" sx={{ borderRadius: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              New guided intake workbench
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Review portal, WhatsApp, and callback-driven intake cases in one queue. This is the
              operational landing place for the new care orchestration front door.
            </Typography>

            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              useFlexGap
              sx={{ alignItems: { md: 'flex-end' } }}
            >
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={draftFilters.status}
                  label="Status"
                  onChange={(event) =>
                    setDraftFilters((current) => ({
                      ...current,
                      status: event.target.value,
                    }))
                  }
                >
                  {STATUS_OPTIONS.map((option) => (
                    <MenuItem key={option.value || 'all-statuses'} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel>Channel</InputLabel>
                <Select
                  value={draftFilters.channel}
                  label="Channel"
                  onChange={(event) =>
                    setDraftFilters((current) => ({
                      ...current,
                      channel: event.target.value,
                    }))
                  }
                >
                  {CHANNEL_OPTIONS.map((option) => (
                    <MenuItem key={option.value || 'all-channels'} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                size="small"
                label="Lead phone"
                value={draftFilters.leadPhone}
                onChange={(event) =>
                  setDraftFilters((current) => ({
                    ...current,
                    leadPhone: event.target.value,
                  }))
                }
                placeholder="+91..."
              />

              <Stack direction="row" spacing={1} useFlexGap>
                <Button variant="contained" onClick={applyFilters}>
                  Apply
                </Button>
                <Button variant="outlined" onClick={clearFilters}>
                  Clear
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {error ? <Alert severity="error">{error}</Alert> : null}

        {loading ? (
          <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : items.length === 0 ? (
          <Alert severity="info">
            No care orchestration cases matched these filters yet.
          </Alert>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: {
                xs: '1fr',
                xl: 'repeat(2, minmax(0, 1fr))',
              },
            }}
          >
            {items.map((item) => (
              <ResultCard
                key={item?.careCase?.id || item?.lead?.id || JSON.stringify(item)}
                item={item}
              />
            ))}
          </Box>
        )}
      </Box>
    </>
  )
}
